import { useState } from "react";
import { AnimatePresence, motion, useScroll, useSpring } from "framer-motion";
import { Menu, ShoppingBag, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const { t, lang, setLang, cartCount, setCartOpen } = useStore();
  const [open, setOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.3 });

  const links = [
    { id: "beranda", label: t.nav.home },
    { id: "koleksi", label: t.nav.catalog },
    { id: "lokasi", label: t.nav.location },
    { id: "kontak", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    scrollTo(id);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-black/80 backdrop-blur-md">
      <motion.div
        data-testid="scroll-progress"
        style={{ scaleX: progress }}
        className="absolute bottom-[-1px] left-0 h-[2px] w-full origin-left bg-gradient-to-r from-ember to-ember-light"
      />
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          data-testid="nav-brand"
          onClick={() => go("beranda")}
          className="flex items-center gap-3"
        >
          <img
            src="/logo.jpg"
            alt="Nusua Betta logo"
            data-testid="logo-mark"
            className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-white/20"
          />
          <span className="flex flex-col items-start leading-none">
            <span className="whitespace-nowrap font-display text-sm font-bold tracking-[0.22em] text-white sm:text-base">
              NUSUA BETTA
            </span>
            <span className="mt-1 hidden whitespace-nowrap font-mono text-[9px] uppercase tracking-[0.35em] text-ember min-[400px]:block">
              Exclusive Betta
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="group relative font-mono text-xs uppercase tracking-[0.2em] text-mist transition-colors hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-ember transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <div
            className="flex items-center rounded-full border border-white/15 p-0.5"
            data-testid="lang-toggle"
          >
            {["id", "en"].map((code) => (
              <button
                key={code}
                data-testid={`lang-toggle-${code}`}
                onClick={() => setLang(code)}
                className={`rounded-full px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-widest transition-colors duration-300 ${
                  lang === code ? "bg-ember text-black" : "text-mist hover:text-white"
                }`}
              >
                {code}
              </button>
            ))}
          </div>

          <button
            data-testid="cart-button"
            onClick={() => setCartOpen(true)}
            className="relative flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white transition-colors hover:border-ember hover:text-ember"
          >
            <ShoppingBag size={17} />
            {cartCount > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-ember px-1 font-mono text-[10px] font-bold text-black"
              >
                {cartCount}
              </span>
            )}
          </button>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white lg:hidden"
            aria-label="Menu"
          >
            {open ? <X size={17} /> : <Menu size={17} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-drawer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-t border-white/10 bg-black/95 lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  initial={{ x: -16, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.08 + i * 0.06 }}
                  onClick={() => go(l.id)}
                  className="flex items-center justify-between border-b border-white/5 py-3 text-left font-display text-lg tracking-wide text-white"
                >
                  {l.label}
                  <span className="font-mono text-[10px] text-ember">0{i + 1}</span>
                </motion.button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
